<template>
  <AdminTable
    ref="table"
    :api-builder="apiBuilder"
    :columns="columns"
    :row-actions="['edit', 'delete']"
    :bulk-actions="[]"
    :on-edit-item="editing"
    :per-page="perPage"
    :per-page-options="perPageOptions"
    :sort-desc="true"
    sort-by="priority"
  >
    <template v-slot:zone_a.name="{ item }">
      <div :class="[item.zone_a ? '' : mutedClass, 'text-right']">
        {{ item.zone_a ? item.zone_a.name : textAny }}
      </div>
    </template>
    <template v-slot:is_both_directions="{ item }">
      <div
        :title="item.is_both_directions ? bothDirectionsLabel : ''"
        class="text-center"
      >
        <i
          :class="[
            item.is_both_directions ? 'icon-transmission' : 'icon-arrow-right7',
            'text-lg',
          ]"
        ></i>
      </div>
    </template>
    <template v-slot:zone_b.name="{ item }">
      <div :class="[item.zone_b ? '' : mutedClass]">
        {{ item.zone_b ? item.zone_b.name : textAny }}
      </div>
    </template>
    <template v-slot:is_fixed="{ item }">
      {{ item.is_fixed ? $t('general.yes') : $t('general.no') }}
    </template>
    <template v-slot:priority="{ item }">
      <div @click="editPrice(item, 'priority')">
        <b-input
          v-if="editingPrice === item.id"
          v-model="item.priority"
          :autofocus="editingPriceField === 'priority'"
          class="p-0"
          size="small"
          type="text"
          @keydown.enter.exact="updatePrice(item)"
          @keydown.esc.stop="cancelEdit()"
        />
        <span v-else v-text="item.priority"></span>
      </div>
    </template>
    <template v-slot:pricing.fixed_price="{ item }">
      <div @click="editPrice(item, 'fixed_price')">
        <b-input
          v-if="editingPrice === item.id"
          v-model="item.pricing.fixed_price"
          :autofocus="editingPriceField === 'fixed_price'"
          class="p-0"
          size="small"
          type="text"
          @keydown.enter.exact="updatePrice(item)"
          @keydown.esc.stop="cancelEdit()"
        />
        <span
          v-else-if="item.pricing.fixed_price"
          v-text="item.pricing.fixed_price"
        ></span>
        <span v-else class="text-muted">---</span>
      </div>
    </template>
    <template v-slot:pricing.one_way="{ item }">
      <div @click="editPrice(item, 'one_way')">
        <b-input
          v-if="editingPrice === item.id"
          v-model="item.pricing.one_way"
          :autofocus="editingPriceField === 'one_way'"
          class="p-0"
          size="small"
          type="text"
          @keydown.enter.exact="updatePrice(item)"
          @keydown.esc.stop="cancelEdit()"
        />
        <span
          v-else-if="item.pricing.one_way"
          v-text="item.pricing.one_way"
        ></span>
        <span v-else class="text-muted">---</span>
      </div>
    </template>
    <template v-slot:pricing.return="{ item }">
      <div @click="editPrice(item, 'return')">
        <b-input
          v-if="editingPrice === item.id"
          v-model="item.pricing.return"
          :autofocus="editingPriceField === 'return'"
          class="p-0"
          size="small"
          type="text"
          @keydown.enter.exact="updatePrice(item)"
          @keydown.esc.stop="cancelEdit()"
        />
        <span
          v-else-if="item.pricing.return"
          v-text="item.pricing.return"
        ></span>
        <span v-else class="text-muted">---</span>
      </div>
    </template>
    <template v-slot:pricing.minimum_price="{ item }">
      <div @click="editPrice(item, 'minimum_price')">
        <b-input
          v-if="editingPrice === item.id"
          v-model="item.pricing.minimum_price"
          :autofocus="editingPriceField === 'minimum_price'"
          class="p-0"
          size="small"
          type="text"
          @keydown.enter.exact="updatePrice(item)"
          @keydown.esc.stop="cancelEdit()"
        />
        <span
          v-else-if="item.pricing.minimum_price"
          v-text="item.pricing.minimum_price"
        ></span>
        <span v-else class="text-muted">---</span>
      </div>
    </template>
  </AdminTable>
</template>

<script>
import { AdminTable } from 'stylemix-vue-admin'

export default {
  name: 'ZoneToZonePricingTable',

  components: {
    AdminTable,
  },

  props: {
    apiBuilder: {
      type: Function,
      required: true,
    },
    perPage: {
      type: Number,
      default: 15,
    },
  },

  data() {
    return {
      editingPrice: null,
      editingPriceField: null,
    }
  },

  computed: {
    columns() {
      return [
        {
          key: 'id',
          label: this.$t('general.id'),
        },
        {
          key: 'zone_a.name',
          label: this.$t('vehicles.attributes.pricing.zone_a.label'),
          thClass: 'text-right',
        },
        {
          key: 'is_both_directions',
          thClass: 'text-center',
          label: '',
        },
        {
          key: 'zone_b.name',
          label: this.$t('vehicles.attributes.pricing.zone_b.label'),
        },
        {
          key: 'priority',
          label: this.$t('vehicles.attributes.pricing.priority.label'),
          sortable: true,
          defaultSortable: true,
        },
        {
          key: 'is_fixed',
          label: this.$t('vehicles.attributes.pricing.is_fixed.label'),
        },
        {
          key: 'pricing.fixed_price',
          label: this.$t('vehicles.attributes.pricing.fixed_price.label'),
          sortable: true,
        },
        {
          key: 'pricing.one_way',
          label: this.$t('vehicles.attributes.pricing.one_way.label'),
          sortable: true,
        },
        {
          key: 'pricing.return',
          label: this.$t('vehicles.attributes.pricing.return.label'),
          sortable: true,
        },
        {
          key: 'pricing.minimum_price',
          label: this.$t('vehicles.attributes.pricing.minimum_price.label'),
          sortable: true,
        },
      ]
    },
    textAny() {
      return `<${this.$t('general.any')}>`
    },
    mutedClass() {
      return {
        'text-muted': true,
        italic: true,
      }
    },
    bothDirectionsLabel() {
      return this.$t('vehicles.attributes.pricing.both_directions.label')
    },
    perPageOptions() {
      return [
        { value: 15, text: 15 },
        { value: 50, text: 50 },
        { value: 100, text: 100 },
        { value: 0, text: this.$t('general.all') },
      ]
    },
  },

  methods: {
    editing(item) {
      this.$emit('edit', item)
    },
    refresh() {
      this.$refs.table.refresh()
    },
    updatePrice(item) {
      this.apiBuilder()
        .update(item.id, {
          pricing: {
            one_way: item.pricing.one_way === '' ? null : item.pricing.one_way,
            return: item.pricing.return === '' ? null : item.pricing.return,
            fixed_price:
              item.pricing.fixed_price === '' ? null : item.pricing.fixed_price,
            minimum_price:
              item.pricing.minimum_price === ''
                ? null
                : item.pricing.minimum_price,
          },
          priority: item.priority,
        })
        .then(() => {
          this.editingPrice = null
        })
        .then(() => this.refresh())
    },
    editPrice(item, field) {
      this.editingPrice = item.id
      this.editingPriceField = field
    },
    cancelEdit() {
      this.editingPrice = null
      this.refresh()
    },
  },
}
</script>
<style>
@media (min-width: 1200px) {
  .modal-xl {
    max-width: 1300px;
  }
}
</style>
